/**
 * PATH: packages/pricelist/src/SupplierRibbon.tsx
 * FILE_ID: PKG-PRICELIST-SUPPLIER-RIBBON-001
 * PURPOSE: Compact supplier information ribbon rendered above the section tab
 *          strip when a specific supplier section is active — supplier name,
 *          ordering contact/email, default discount and price-list date.
 * WHY: 2026-07-14 meeting task 7 — users need the supplier's ordering details
 *      and the price-list effective date in view while working in that
 *      supplier's tab; it must switch with the tab (no stale info).
 * RELATED: PricelistWorkbook.tsx (host), types.ts (supplier_info payload)
 */
import { Building2, CalendarDays, Mail, Percent, User } from 'lucide-react'
import type { PricelistSection } from './types'
import type { GridRow } from './columns'

export interface SupplierRibbonProps {
  section: PricelistSection
  rows: GridRow[]
}

/** Supplier-level price-list date: the value shared by the section's rows. */
export function supplierPriceListDate(rows: GridRow[]): string {
  for (const row of rows) {
    const value = String(row.price_list_date ?? '').trim()
    if (value) return value
  }
  return ''
}

export default function SupplierRibbon({ section, rows }: SupplierRibbonProps) {
  const info = section.supplier_info
  const name = info?.name || section.supplier_name || section.name
  const settings = (section.settings ?? {}) as { supplier_panel?: Record<string, unknown> }
  const panel = settings.supplier_panel ?? {}
  const discount = panel.default_discount != null && panel.default_discount !== ''
    ? `${panel.default_discount}%`
    : ''
  const date = String(panel.price_list_date ?? '').trim() || supplierPriceListDate(rows)
  // Stamped by apply_discount (task 3) whenever supplier pricing changes.
  const lastUpdate = String(panel.last_update ?? '').trim()

  const item = (icon: React.ReactNode, label: string, value: string) => (
    <span className="flex items-center gap-1.5" title={label}>
      {icon}
      <span className="text-slate-400">{label}:</span>
      <span className="font-medium text-slate-700 dark:text-slate-200">{value || '—'}</span>
    </span>
  )

  return (
    <div
      data-testid="supplier-ribbon"
      className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300"
    >
      {item(<Building2 className="h-3.5 w-3.5 text-slate-400" />, 'Supplier', name)}
      {item(<User className="h-3.5 w-3.5 text-slate-400" />, 'Ordering contact', info?.contact_name || '')}
      {item(<Mail className="h-3.5 w-3.5 text-slate-400" />, 'Ordering email', info?.contact_email || '')}
      {item(<Percent className="h-3.5 w-3.5 text-slate-400" />, 'Default discount', discount)}
      {item(<CalendarDays className="h-3.5 w-3.5 text-slate-400" />, 'Price list', date)}
      {lastUpdate && item(<CalendarDays className="h-3.5 w-3.5 text-slate-400" />, 'Last update', lastUpdate)}
    </div>
  )
}
